/**
 * GENEL ONAY DİYALOĞU — tarayıcının çirkin window.confirm popup'ı YERİNE site
 * şablonuyla açılan onay kutusu. Maliyet diyaloğuyla (cost-confirm) aynı desen:
 * tek bir <ConfirmDialogHost /> App kökünde durur, her çağrı onu açar.
 *
 * Kullanım:
 *   if (!(await confirmDialog({ title: "8 madde takvime yerleştirilecek", lines: ["…"] }))) return;
 */
import { useSyncExternalStore } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CircleHelp, Trash2 } from "lucide-react";

export type ConfirmOptions = {
  title: string;
  /** Kısa açıklama paragrafı (opsiyonel) */
  description?: string;
  /** Madde madde listelenecek satırlar (opsiyonel) */
  lines?: string[];
  confirmText?: string;
  cancelText?: string;
  /** true → silme gibi yıkıcı işlem: kırmızı vurgu + çöp ikonu */
  danger?: boolean;
};

type Pending = { opts: ConfirmOptions; resolve: (ok: boolean) => void };
let pending: Pending | null = null;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Onay kutusunu açar; kullanıcı onaylarsa true döner. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    pending = { opts, resolve };
    emit();
  });
}

function usePending(): Pending | null {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => subs.delete(cb); },
    () => pending,
  );
}

export function ConfirmDialogHost() {
  const p = usePending();
  if (!p) return null;
  const o = p.opts;
  const accent = o.danger ? "#d92d20" : "#e83e3c";
  const Icon = o.danger ? Trash2 : CircleHelp;
  const close = (ok: boolean) => {
    const resolve = p.resolve;
    pending = null;
    emit();
    resolve(ok);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={() => close(false)}>
      <Card style={{ maxWidth: 440, width: "100%", borderColor: `${accent}55` }} onClick={(ev) => ev.stopPropagation()}>
        <CardContent className="p-5">
          <div className="flex items-start gap-2.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}14` }}>
              <Icon className="w-4.5 h-4.5" style={{ color: accent, width: 18, height: 18 }} />
            </span>
            <div className="min-w-0">
              <div className="font-bold leading-snug">{o.title}</div>
              {o.description && <p className="text-[13px] text-muted-foreground mt-1">{o.description}</p>}
            </div>
          </div>

          {o.lines && o.lines.length > 0 && (
            <ul className="mt-3 rounded-xl px-3.5 py-2.5 space-y-1 text-[12.5px]" style={{ background: `${accent}0a`, border: `1px solid ${accent}30` }}>
              {o.lines.map((l, i) => (
                <li key={i} className="flex gap-1.5">
                  <span style={{ color: accent }}>•</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-end gap-2 mt-4">
            <Button size="sm" variant="ghost" onClick={() => close(false)}>{o.cancelText ?? "Vazgeç"}</Button>
            <Button size="sm" onClick={() => close(true)} className="text-white" style={{ background: accent }}>
              {o.confirmText ?? "Onayla"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
